import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import FinestraAccesso from '../Header/components/auth/FinestraAccesso';
import AvvisoSessione from '../Header/components/AvvisoSessione';
import {useSkin} from '../../moduli/useSkin';
import {pieDiPagina, sezioniAccese, sfondi, testata} from '../../moduli/leggi';
import {leggiCifreConto} from '../../utils/conto';
import TestataNova from './TestataNova';
import MenuNova from './MenuNova';
import BarraNova from './BarraNova';
import {FoglioAltro, FoglioCassa, FoglioConto, FoglioFornitori, FoglioGiochi, FoglioRegistrati, FoglioSchedina} from './FogliNova';
import PiedeNova from './PiedeNova';
import RisaliNova from './RisaliNova';
import useCimaPagina from './useCimaPagina';
import Effetti from '../Effetti/Effetti';
import {annunciaBonus} from '../../effetti';
import './Nova.css';

/**
 * IL GUSCIO NOVA: testata, menu, barra in basso e piede di pagina disegnati
 * come in Nova, attorno alle pagine di sempre (`children`). Lo sceglie la
 * skin dal modulo Testata (forma › stile = «nova»); le altre skin tengono la
 * striscia, la riga dei giochi, la barra e il piede di sempre.
 *
 * Il guscio di sempre (BarraAlta) fa tre cose che qui non si possono
 * perdere, e che la striscia Nova riscrive in piccolo:
 *   - la FINESTRA DI ACCESSO: una sola per tutto il sito, la aprono la testata,
 *     il menu, la barra e chiunque la chieda al servizio (accessoRichiesto);
 *     si riapre da sola, col motivo scritto, quando la sessione finisce
 *     (fineSessione);
 *   - il SALDO: dalle cifre del conto, riletto quando il conto cambia
 *     (balanceUpdated, giocataCambiata, walletSubject);
 *   - l'USCITA: chiude la sessione e riporta alla Home.
 * Con la skin Nova nessuna delle tre e' una seconda copia da tenere allineata:
 * sono le stesse chiamate, montate dal guscio che c'e'.
 *
 * DOVE STA IL MENU DAL COMPUTER (modulo Testata › Menu): di serie «in
 * testata» (classe `nova-orizzontale`): niente colonna a sinistra, le sezioni
 * e le schede stanno in alto accanto al logo, la pagina e' larga tutto lo
 * schermo (Testata › Larghezza = «Tutto lo schermo», `--lvc-pagina-max:
 * none`) e il fondo sfuma dal colore della skin verso il basso. Con «Colonna
 * a sinistra» il menu e' la colonna fissa di prima, a scomparsa se il modulo
 * lo dice (classe `scomparsa`: dal computer col mouse il menu e' una colonna
 * stretta di icone che si apre sopra la pagina al passaggio del mouse). Lo fa
 * il CSS (Nova.css): qui si mettono le classi e basta. Sul telefono e sul
 * tablet la testata e' come quella del computer, senza hamburger ne' menu a
 * sinistra (le sezioni nella sua seconda riga, TestataNova), e c'e' la barra
 * in basso: le pagine in piu' stanno in «Altro», i fornitori dentro «Giochi»
 * (BarraNova). Il cassetto del menu sul telefono non si apre piu'.
 *
 * IL CARATTERE: con Nova il testo e' in Plus Jakarta Sans quando la skin non
 * ha scritto un carattere suo (Sito › Skin, --lvc-carattere-skin). Il file del
 * carattere lo chiede useCarattereNova, una volta, solo dentro questo guscio:
 * il guscio di sempre non lo scarica.
 *
 * LA BARRA E I PANNELLI DEL TELEFONO: un solo pannello aperto per volta, fra
 * il menu, la finestra di accesso e i pannelli dal basso (Altro, Giochi coi
 * suoi Fornitori, Conto, Cassa, Registrati). Aprirne uno chiude gli altri; cambiando pagina, o
 * entrando e uscendo dal conto, si chiudono tutti. Il saldo censurato e'
 * qui per lo stesso motivo: testata e pannelli mostrano lo stesso occhio.
 *
 * OGNI PAGINA NUOVA SI APRE DALL'ALTO (useCimaPagina): cliccando una
 * categoria, una voce della testata o del piede, o il collegamento della
 * pagina in cui si e' gia'. «Indietro» riporta invece dove si era.
 *
 * GLI EFFETTI (modulo Effetti, components/Effetti): la pioggia di monete al
 * versamento accreditato (la manda la Cassa) e al bonus nuovo (lo vede
 * questo guscio, confrontando i bonus attivi fra una lettura del conto e
 * l'altra). Montati qui, una volta, sopra tutto.
 *
 * Il contenitore tiene la classe `main-containers`: il CSS delle pagine sport
 * (lista eventi) conta su `.main-containers > .main-container`, quindi le
 * pagine restano figlie dirette.
 */
/* Il carattere di serie di Nova (Nova.css: `var(--lvc-carattere-skin, 'Plus Jakarta Sans', …)`). */
const CARATTERE_NOVA = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap';

/** Chiede il foglio del carattere Plus Jakarta Sans, una volta sola per pagina. */
function useCarattereNova() {
  React.useEffect(() => {
    if (typeof document === 'undefined' || document.querySelector('link[data-nova-carattere]')) return;
    const pre = document.createElement('link');
    pre.rel = 'preconnect';
    pre.href = 'https://fonts.gstatic.com';
    pre.crossOrigin = 'anonymous';
    const foglio = document.createElement('link');
    foglio.rel = 'stylesheet';
    foglio.href = CARATTERE_NOVA;
    foglio.dataset.novaCarattere = '1';
    document.head.appendChild(pre);
    document.head.appendChild(foglio);
  }, []);
}

/* QUANTE QUOTE CI SONO NELLA SCHEDINA: il numero sul tasto Schedina.
 *
 * Lo ascolta solo chi lo mostra. Prima lo teneva il guscio, e a ogni quota
 * toccata si ridisegnava tutto: testata, menu, piede, i fogli. Adesso si
 * ridisegnano la barra in basso e il foglio della schedina. */
function ConQuante({userService, children}) {
  const [quante, setQuante] = React.useState(0);
  React.useEffect(() => {
    if (!userService || typeof userService.selezioni !== 'function') return undefined;
    const sub = userService.selezioni().subscribe((n) => setQuante(n || 0));
    return () => sub.unsubscribe();
  }, [userService]);
  return children(quante);
}

export default function GuscioNova({userService, children}) {
  const location = useLocation();
  const navigate = useNavigate();
  useSkin();
  useCimaPagina();

  const [menu, setMenu] = React.useState(false);
  const [dentro, setDentro] = React.useState(false);
  const [conto, setConto] = React.useState(null);
  const [accesso, setAccesso] = React.useState(false);
  const [motivo, setMotivo] = React.useState('barra');
  const [dettaglio, setDettaglio] = React.useState(null);
  const [pannello, setPannello] = React.useState(null);   // 'altro' | 'giochi' | 'fornitori' | 'conto' | 'versa' | 'registrati' | 'schedina'
  // la query con cui si e' chiesto di registrarsi (?code=… da Telegram, ?invito=…): la legge il modulo
  const [richiestaRegistrazione, setRichiestaRegistrazione] = React.useState({ricerca: '', telegram: null, avviso: false});
  const [saldoVisibile, setSaldoVisibile] = React.useState(false);

  /* Il pannello da aprire appena arrivati in una pagina: la Schedina toccata
     fuori dallo sport porta allo sport e si apre li'. */
  const dopoIlCambio = React.useRef(null);

  /* Cambiando pagina il cassetto e i pannelli si chiudono. */
  React.useEffect(() => {
    setMenu(false);
    setPannello(dopoIlCambio.current);
    dopoIlCambio.current = null;
  }, [location.pathname, location.search]);
  /* Entrando o uscendo, anche; e uscendo il saldo torna nascosto. */
  React.useEffect(() => {
    setPannello(null);
    if (!dentro) setSaldoVisibile(false);
  }, [dentro]);

  const apriAccesso = React.useCallback(() => {
    setMenu(false);
    setPannello(null);
    setMotivo('barra');
    setDettaglio(null);
    setAccesso(true);
  }, []);

  React.useEffect(() => {
    if (!userService || !userService.accessoRichiesto) return undefined;
    const sub = userService.accessoRichiesto().subscribe(() => apriAccesso());
    return () => sub.unsubscribe();
  }, [userService, apriAccesso]);

  /* /register (RegistrazioneDaLink) e il ritorno da Telegram chiedono di
     registrarsi: si apre il pannello, con la query da cui si viene. */
  React.useEffect(() => {
    if (!userService || typeof userService.registrazioneRichiesta !== 'function') return undefined;
    const sub = userService.registrazioneRichiesta().subscribe((r) => {
      setMenu(false);
      if (accesso) chiudiAccesso();
      setRichiestaRegistrazione({ricerca: (r && r.ricerca) || '', telegram: (r && r.telegram) || null, avviso: !!(r && r.avviso)});
      setPannello('registrati');
    });
    return () => sub.unsubscribe();
    // chiudiAccesso cambia a ogni disegno: basta riascoltare quando cambia il servizio
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userService]);

  React.useEffect(() => {
    if (!userService || typeof userService.fineSessione !== 'function') return undefined;
    const sub = userService.fineSessione().subscribe((perche) => {
      if (!perche) return;
      setMotivo('scaduta');
      setDettaglio(perche);
      setAccesso(true);
    });
    return () => sub.unsubscribe();
  }, [userService]);

  React.useEffect(() => {
    if (!userService || typeof userService.isLoggedIn !== 'function') return undefined;
    const stop = new Subject();
    const rileggi = () => leggiCifreConto().then((d) => { if (d) setConto(d); });
    userService.isLoggedIn().pipe(takeUntil(stop)).subscribe((acceso) => {
      setDentro(!!acceso);
      if (acceso) rileggi(); else setConto(null);
    });
    window.addEventListener('balanceUpdated', rileggi);
    window.addEventListener('giocataCambiata', rileggi);
    if (userService.walletSubject && typeof userService.walletSubject.subscribe === 'function') {
      userService.walletSubject.pipe(takeUntil(stop)).subscribe(() => rileggi());
    }
    return () => {
      window.removeEventListener('balanceUpdated', rileggi);
      window.removeEventListener('giocataCambiata', rileggi);
      stop.next();
      stop.complete();
    };
  }, [userService]);

  /* LA FESTA DEL BONUS (modulo Effetti): quando fra i bonus attivi del conto
     ne compare uno in piu' rispetto all'ultima lettura. La prima lettura non
     festeggia (i bonus che c'erano gia'), e uscendo si riparte da zero. */
  const bonusVisti = React.useRef(null);
  React.useEffect(() => {
    const attivi = conto && Array.isArray(conto.bonusAttivi) ? conto.bonusAttivi : null;
    if (!attivi) { bonusVisti.current = null; return; }
    const prima = bonusVisti.current;
    bonusVisti.current = attivi;
    if (prima && attivi.length > prima.length) {
      const nuovo = attivi[attivi.length - 1] || {};
      annunciaBonus({importo: nuovo.importo, valuta: conto.currency});
    }
  }, [conto]);

  const esci = () => {
    try {
      userService.doLogout(window.localStorage.getItem('CLIENT_SESSION'));
    } finally {
      setConto(null);
      navigate('/');
    }
  };
  const chiudiAccesso = () => {
    setAccesso(false);
    setDettaglio(null);
    setMotivo('barra');
    if (typeof userService?.consumaFineSessione === 'function') userService.consumaFineSessione();
  };
  /* Un tasto della barra: apre il suo, chiude gli altri; ritoccato, richiude. null chiude tutto. */
  const tasto = (k) => {
    const giaAperto = (k === 'menu' && menu) || (k === 'accedi' && accesso) || (k !== null && pannello === k);
    setMenu(false);
    setPannello(null);
    if (accesso && k !== 'accedi') chiudiAccesso();
    if (!k || giaAperto) {
      if (k === 'accedi') chiudiAccesso();
      return;
    }
    if (k === 'menu') setMenu(true);
    else if (k === 'accedi') apriAccesso();
    else if (k === 'schedina' && !location.pathname.startsWith('/sport')) {
      /* La schedina vera sta nella pagina delle quote: ci si va, e si apre li'. */
      dopoIlCambio.current = 'schedina';
      navigate('/sport');
    } else setPannello(k);
  };
  const chiudiPannello = React.useCallback(() => setPannello(null), []);
  const aperto = menu ? 'menu' : accesso ? 'accedi' : pannello;
  const occhio = () => setSaldoVisibile((v) => !v);

  const forma = testata();
  const orizzontale = forma.menu === 'testata';
  const conSport = sezioniAccese().some((s) => s.chiave === 'sport');
  useCarattereNova();

  /* Gli sfondi del sito (Sito › Skin › Sfondi del sito): l'alto e il corpo
     sono due strati dietro tutto, il piede li prende dal suo ::before. Senza
     immagini niente strati e niente variabili: resta il colore Sfondo. */
  const fondi = sfondi();
  const stile = {'--lvc-pagina-max': forma.larghezza === 'piena' ? 'none' : forma.larghezzaPagina + 'px'};
  Object.entries(fondi).forEach(([zona, f]) => {
    if (f.computer) stile['--n-sfondo-' + zona] = 'url("' + f.computer + '")';
    if (f.telefono) stile['--n-sfondo-' + zona + '-tel'] = 'url("' + f.telefono + '")';
  });
  const conAlto = !!(fondi.alto && (fondi.alto.computer || fondi.alto.telefono));
  const conPagina = !!(fondi.pagina && (fondi.pagina.computer || fondi.pagina.telefono));
  const conPiede = !!(fondi.piede && (fondi.piede.computer || fondi.piede.telefono));

  return (
    <div className={'main-containers nova'
      + (orizzontale ? ' nova-orizzontale' : '')
      + (orizzontale && forma.tasti === 'rilievo' ? ' nova-tasti-rilievo' : '')
      + (!orizzontale && forma.menuAScomparsa ? ' scomparsa' : '')
      + (conAlto || conPagina || conPiede ? ' con-sfondi' : '')
      + (conPiede ? ' con-sfondo-piede' : '')}
      style={stile}>
      {conPagina && <div className={'nova-sfondo-pagina'} aria-hidden={'true'}/>}
      {conAlto && <div className={'nova-sfondo-alto'} aria-hidden={'true'}/>}
      <TestataNova userService={userService} dentro={dentro} conto={conto} orizzontale={orizzontale}
        onAccedi={apriAccesso} onEsci={esci}
        saldoVisibile={saldoVisibile} onOcchio={occhio}/>
      <MenuNova aperto={menu} onChiudi={() => setMenu(false)} dentro={dentro}/>
      {children}
      <PiedeNova/>
      {pieDiPagina().risali.acceso && <RisaliNova nascosta={!!aperto}/>}
      <ConQuante userService={userService}>
        {(quante) => <BarraNova dentro={dentro} aperto={aperto} onTasto={tasto} quante={quante}/>}
      </ConQuante>
      <FoglioAltro aperto={pannello === 'altro'} onChiudi={chiudiPannello}/>
      <FoglioGiochi aperto={pannello === 'giochi'} onChiudi={chiudiPannello} onFornitori={() => setPannello('fornitori')}
        conPagine={conSport}/>
      {conSport && (
        <ConQuante userService={userService}>
          {(quante) => <FoglioSchedina aperto={pannello === 'schedina'} onChiudi={chiudiPannello} quante={quante}/>}
        </ConQuante>
      )}
      <FoglioFornitori aperto={pannello === 'fornitori'} onChiudi={chiudiPannello} onIndietro={() => setPannello('giochi')}/>
      {dentro ? (
        <>
          <FoglioConto aperto={pannello === 'conto'} onChiudi={chiudiPannello} conto={conto} onEsci={esci}
            nome={userService && typeof userService.getUsername === 'function' ? userService.getUsername() : ''}
            saldoVisibile={saldoVisibile} onOcchio={occhio}/>
          <FoglioCassa aperto={pannello === 'versa'} onChiudi={chiudiPannello} conto={conto}
            saldoVisibile={saldoVisibile} onOcchio={occhio}/>
        </>
      ) : (
        <FoglioRegistrati aperto={pannello === 'registrati'} onChiudi={chiudiPannello} onAccedi={() => tasto('accedi')}
          userService={userService} ricerca={richiestaRegistrazione.ricerca} telegram={richiestaRegistrazione.telegram} avviso={richiestaRegistrazione.avviso}/>
      )}

      <FinestraAccesso
        aperta={accesso}
        motivo={motivo}
        dettaglio={dettaglio}
        userService={userService}
        onChiudi={chiudiAccesso}
        onRegistrati={() => tasto('registrati')}/>
      <AvvisoSessione userService={userService}/>
      <Effetti/>
    </div>
  );
}
